'use client';

import { IconAlertCircle, IconBell, IconCheck, IconLoader2 } from '@tabler/icons-react';
import { useState } from 'react';

import { usePushNotifications } from '../hooks/usePushNotifications';

export interface ClientPushPromptCardProps {
  ticketId: string;
  ticketNumber?: string;
  onSubscribed?: () => void;
}

export function ClientPushPromptCard({
  ticketId,
  ticketNumber,
  onSubscribed,
}: ClientPushPromptCardProps) {
  const {
    isSupported,
    permission,
    isLoading,
    subscribeClientTicket,
  } = usePushNotifications();

  const [isDone, setIsDone] = useState(false);

  if (!isSupported) {
    return null;
  }

  const handleEnable = async () => {
    const success = await subscribeClientTicket(ticketId);
    if (success) {
      setIsDone(true);
      onSubscribed?.();
    }
  };

  if (isDone || permission === 'granted') {
    return (
      <div className="w-full bg-emerald-950/40 border border-emerald-700/60 rounded-xl p-4 flex items-center gap-3 text-left">
        <div className="w-9 h-9 rounded-lg bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center text-emerald-400 shrink-0">
          <IconCheck size={20} />
        </div>
        <div className="space-y-0.5">
          <div className="text-sm font-bold text-white">
            Powiadomienia Web Push aktywne!
          </div>
          <div className="text-xs text-zinc-300">
            Gdy serwis odpisze na wiadomość {ticketNumber ? `(${ticketNumber})` : ''}, otrzymasz powiadomienie w tej przeglądarce.
          </div>
        </div>
      </div>
    );
  }

  if (permission === 'denied') {
    return (
      <div className="w-full bg-zinc-950/60 border border-zinc-800 rounded-xl p-3.5 flex items-center gap-2.5 text-left text-xs text-zinc-400">
        <IconAlertCircle size={18} className="text-amber-400 shrink-0" />
        <span>
          Powiadomienia w przeglądarce są zablokowane. Aby je otrzymać, zezwól na powiadomienia w ustawieniach strony.
        </span>
      </div>
    );
  }

  return (
    <div className="w-full bg-zinc-950/60 border border-[#eded1e]/30 rounded-xl p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-left shadow-lg">
      <div className="flex items-start gap-3.5">
        <div className="w-10 h-10 rounded-xl bg-[#eded1e]/15 border border-[#eded1e]/30 flex items-center justify-center text-[#eded1e] shrink-0 mt-0.5 sm:mt-0">
          <IconBell size={22} />
        </div>
        <div className="space-y-1">
          <div className="text-sm sm:text-base font-bold text-white">
            Powiadomienie o odpowiedzi serwisu
          </div>
          <div className="text-xs text-zinc-300 leading-relaxed">
            Chcesz otrzymać powiadomienie w przeglądarce, gdy serwis odpisze na Twoją wiadomość?
          </div>
        </div>
      </div>
      <button
        type="button"
        disabled={isLoading}
        onClick={handleEnable}
        className="shrink-0 self-stretch sm:self-auto inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-[#eded1e] hover:bg-[#f6f62a] text-zinc-950 font-bold text-xs transition-all cursor-pointer shadow-sm disabled:opacity-50"
      >
        {isLoading ? (
          <>
            <IconLoader2 size={16} className="animate-spin" />
            <span>Włączanie...</span>
          </>
        ) : (
          <>
            <IconBell size={16} />
            <span>Włącz powiadomienia</span>
          </>
        )}
      </button>
    </div>
  );
}
